import { FormEventHandler, useEffect, useState } from 'react';
import { useForm } from '@inertiajs/react';

import { GuestLayout } from '@/Layouts/GuestLayout';
import { InputError } from '@/Components/InputError';
import { PrimaryButton } from '@/Components/PrimaryButton';
import TextInput from '@/Components/TextInput';
import { InputLabel } from '@/Components/InputLabel';
import { Head } from '@/Components/Head';

const SUCCESS_RESET_STATE = 'reset-password/success';
const ERROR_RESET_STATE = 'reset-password/error';
const INITIAL_RESET_STATE = 'reset-password/initial';

const resetStates = [
    SUCCESS_RESET_STATE,
    ERROR_RESET_STATE,
    INITIAL_RESET_STATE,
] as const;

type ResetState = (typeof resetStates)[number];

export default function ForgotPassword({ status }: { status?: string }) {
    const { data, setData, post, processing, errors } = useForm({
        email: '',
    });

    const [resetState, setResetState] = useState<ResetState>(
        status ? SUCCESS_RESET_STATE : INITIAL_RESET_STATE,
    );

    useEffect(() => {
        if (status) {
            setResetState(SUCCESS_RESET_STATE);
        }
    }, [status]);

    useEffect(() => {
        if (errors && errors.email) {
            setResetState(ERROR_RESET_STATE);
        }
    }, [errors]);

    const submit: FormEventHandler = (e) => {
        e.preventDefault();

        post(route('password.email'));
    };

    return (
        <GuestLayout>
            <Head title="ReelFlow Forgot Password" />
            {resetState === SUCCESS_RESET_STATE ? (
                <>
                    <h4 className="text-heading-four text-gray-600">
                        Now, check your email...
                    </h4>
                    <div className="text-sm text-gray-600">
                        We have sent you a link to reset your password.
                    </div>
                    <div className="text-sm text-gray-600">
                        Haven’t received it?{' '}
                        <span
                            className="underline cursor-pointer"
                            onClick={() => {
                                setResetState(INITIAL_RESET_STATE);
                            }}>
                            Click here to try again
                        </span>
                    </div>
                    <div className="text-sm text-gray-600">
                        For help,{' '}
                        <a
                            className="transition-all underline hover:text-brand-teal"
                            target="_blank"
                            href="https://help.reelflow.com">
                            visit our help center
                        </a>
                        .
                    </div>
                </>
            ) : (
                <>
                    <h4 className="text-heading-four text-gray-600">
                        Reset your password
                    </h4>
                    <div className="text-sm text-gray-600">
                        Forgot your password? No problem.
                    </div>
                    <div className="text-sm text-gray-600">
                        Just let us know your email address and we will email
                        you a password reset link that will allow you to choose
                        a new one.
                    </div>

                    <form onSubmit={submit} className="grid gap-4">
                        <div>
                            <InputLabel htmlFor="email" value="Work Email" />

                            <TextInput
                                autoFocus
                                id="email"
                                type="email"
                                name="email"
                                value={data.email}
                                className="mt-1 block w-full"
                                onChange={(e) =>
                                    setData('email', e.target.value)
                                }
                            />

                            <InputError
                                message={errors.email}
                                className="mt-2"
                            />
                        </div>

                        <div>
                            <PrimaryButton disabled={processing}>
                                Email Password Reset Link
                            </PrimaryButton>
                        </div>
                    </form>
                </>
            )}
        </GuestLayout>
    );
}
